import type { ComponentType } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  ArrowRightLeft,
  Building2,
  CalendarCheck2,
  CalendarDays,
  CheckCircle2,
  CircleDashed,
  Clock,
  CloudDownload,
  Flag,
  HardHat,
  ListChecks,
  MessageCircleReply,
  MessageSquare,
  Paperclip,
  Pause,
  Pencil,
  PencilLine,
  Play,
  Plus,
  RotateCcw,
  Send,
  Square,
  Tag,
  Ticket,
  Timer,
  Trash2,
  TriangleAlert,
  UserRound,
  Wrench,
  XCircle,
} from 'lucide-react';
import type { RecentTicketEvent, TicketEventType } from '@erp/shared';
import { useAuthUser } from '@/auth/use-auth';
import { EmptyState, Skeleton } from '@/components/ui/misc';
import { cn, formatMinutes } from '@/lib/utils';
import { MyWeekAgendaSection } from '@/features/agenda/my-week-agenda';
import {
  deskOpenCountsQueryOptions,
  desksQueryOptions,
  myHoursQueryOptions,
  recentEventsQueryOptions,
  ticketsSummaryQueryOptions,
} from './dashboard-queries';

type IconType = ComponentType<{ className?: string }>;

// ── KPI inline (label 13px acima, ícone + número 20px/600 colorido) ─────────
function Kpi({
  icon: Icon,
  label,
  value,
  colorClass,
  loading,
}: {
  icon: IconType;
  label: string;
  value: number | null | undefined;
  colorClass: string;
  loading: boolean;
}) {
  return (
    <div className="flex min-w-28 flex-col gap-1">
      <span className="text-[13px] text-muted-foreground">{label}</span>
      {loading ? (
        <Skeleton className="h-7 w-16" />
      ) : (
        <span
          className={cn(
            'flex items-center gap-1.5 text-xl leading-7 font-semibold tabular-nums',
            value == null ? 'text-muted-foreground/50' : colorClass
          )}
        >
          <Icon className="size-[18px] shrink-0" />
          {value ?? '—'}
        </span>
      )}
    </div>
  );
}

// ── Feed de eventos: ícone/cor/rótulo por tipo ──────────────────────────────
const EVENT_META: Record<TicketEventType, { icon: IconType; label: string; tint: string }> = {
  created: { icon: Plus, label: 'Ticket criado', tint: 'bg-primary/10 text-primary-pressed' },
  updated: { icon: Pencil, label: 'Ticket alterado', tint: 'bg-accent text-muted-foreground' },
  stage_changed: { icon: ListChecks, label: 'Mudança de estágio', tint: 'bg-info/10 text-info' },
  status_changed: { icon: Tag, label: 'Mudança de status', tint: 'bg-info/10 text-info' },
  priority_changed: {
    icon: Flag,
    label: 'Mudança de prioridade',
    tint: 'bg-warning/10 text-warning',
  },
  responsible_changed: {
    icon: UserRound,
    label: 'Responsável alterado',
    tint: 'bg-info/10 text-info',
  },
  reparented: { icon: Building2, label: 'Cliente alterado', tint: 'bg-accent text-muted-foreground' },
  desk_moved: {
    icon: ArrowRightLeft,
    label: 'Movido de mesa',
    tint: 'bg-accent text-muted-foreground',
  },
  closed: { icon: CheckCircle2, label: 'Ticket fechado', tint: 'bg-success/10 text-success' },
  canceled: { icon: XCircle, label: 'Ticket cancelado', tint: 'bg-destructive/10 text-destructive' },
  reopened: { icon: RotateCcw, label: 'Ticket reaberto', tint: 'bg-warning/10 text-warning' },
  comment_added: { icon: MessageSquare, label: 'Nova comunicação', tint: 'bg-info/10 text-info' },
  appointment_added: { icon: Clock, label: 'Apontamento criado', tint: 'bg-info/10 text-info' },
  appointment_deleted: {
    icon: Trash2,
    label: 'Apontamento removido',
    tint: 'bg-destructive/10 text-destructive',
  },
  // pedido da PoolTrack (02/10)
  appointment_edited: { icon: PencilLine, label: 'Apontamento editado', tint: 'bg-info/10 text-info' },
  timer_started: { icon: Play, label: 'Timer iniciado', tint: 'bg-success/10 text-success' },
  timer_stopped: { icon: Square, label: 'Timer parado', tint: 'bg-warning/10 text-warning' },
  sla_paused: { icon: Pause, label: 'SLA pausado', tint: 'bg-info/10 text-info' },
  sla_resumed: { icon: Play, label: 'SLA retomado', tint: 'bg-info/10 text-info' },
  file_added: { icon: Paperclip, label: 'Anexo adicionado', tint: 'bg-info/10 text-info' },
  file_deleted: {
    icon: Paperclip,
    label: 'Anexo removido',
    tint: 'bg-destructive/10 text-destructive',
  },
  service_order_created: {
    icon: Wrench,
    label: 'Ordem de serviço gerada',
    tint: 'bg-primary/10 text-primary',
  },
  // spec 026 — fora do feed recente (RECENT_FEED_HIDDEN_EVENT_TYPES), mas o mapa é total
  tiflux_pulled: { icon: CloudDownload, label: 'Puxado do TiFlux', tint: 'bg-info/10 text-info' },
  // spec 043 — idem: o espelho encerrou o que já tinha acabado no TiFlux
  tiflux_closed: { icon: CloudDownload, label: 'Encerrado no TiFlux', tint: 'bg-accent text-muted-foreground' },
  // técnico responsável em campo (pedido da PoolTrack, 02/10)
  technician_changed: { icon: HardHat, label: 'Técnico alterado', tint: 'bg-info/10 text-info' },
  // spec 014 — disparo por ticket
  dispatch_sent: { icon: Send, label: 'Disparo realizado', tint: 'bg-info/10 text-info' },
  dispatch_failed: {
    icon: TriangleAlert,
    label: 'Disparo falhou',
    tint: 'bg-destructive/10 text-destructive',
  },
  dispatch_replied: {
    icon: MessageCircleReply,
    label: 'Cliente respondeu ao disparo',
    tint: 'bg-success/10 text-success',
  },
  // spec 022 — a API não manda este tipo ao feed (RECENT_FEED_HIDDEN_EVENT_TYPES);
  // a entrada existe porque o mapa cobre todos os tipos
  bot_note_added: { icon: MessageSquare, label: 'Nota do bot', tint: 'bg-accent text-muted-foreground' },
};

/** tipo que esta versão da tela não conhece (a API sobe antes do SPA): o feed não quebra */
const UNKNOWN_EVENT_META = { icon: Pencil, label: 'Atualização', tint: 'bg-accent text-muted-foreground' };

function relativeTime(value: string | Date): string {
  const d = new Date(value);
  const minutes = Math.floor((Date.now() - d.getTime()) / 60_000);
  if (minutes < 1) return 'agora';
  if (minutes < 60) return `há ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `há ${hours} h`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'ontem';
  if (days < 7) return `há ${days} dias`;
  return d.toLocaleDateString('pt-BR');
}

function ActivityItem({ event }: { event: RecentTicketEvent }) {
  const meta = EVENT_META[event.type] ?? UNKNOWN_EVENT_META;
  const Icon = meta.icon;
  return (
    <li className="flex gap-3 border-b border-divider py-2.5 last:border-0 last:pb-0 first:pt-0">
      <span
        className={cn(
          'mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full',
          meta.tint
        )}
      >
        <Icon className="size-3.5" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <span className="truncate text-sm font-semibold">{meta.label}</span>
          <span className="shrink-0 text-xs text-muted-foreground">
            {relativeTime(event.createdAt)}
          </span>
        </div>
        <div className="truncate text-[13px]">
          <Link
            to="/tickets/$ticketNumber"
            params={{ ticketNumber: String(event.ticketNumber) }}
            className="font-medium text-primary hover:text-primary-pressed hover:underline"
          >
            #{event.ticketNumber}
          </Link>
          <span className="text-muted-foreground"> — {event.ticketTitle}</span>
        </div>
        {event.userName ? (
          <div className="text-xs text-muted-foreground">por {event.userName}</div>
        ) : null}
      </div>
    </li>
  );
}

// ── Bloco de horas (Ontem / Hoje / Semana) ──────────────────────────────────
function HoursStat({
  icon: Icon,
  label,
  minutes,
  colorClass,
  loading,
}: {
  icon: IconType;
  label: string;
  minutes: number | undefined;
  colorClass: string;
  loading: boolean;
}) {
  return (
    <div className="flex min-w-24 flex-col gap-1">
      <span className="text-[13px] text-muted-foreground">{label}</span>
      {loading ? (
        <Skeleton className="h-7 w-16" />
      ) : (
        <span
          className={cn(
            'flex items-center gap-1.5 text-xl leading-7 font-semibold tabular-nums',
            colorClass
          )}
        >
          <Icon className="size-[18px] shrink-0" />
          {formatMinutes(minutes ?? 0)}
        </span>
      )}
    </div>
  );
}

export function DashboardPage() {
  const user = useAuthUser();
  const summary = useQuery(ticketsSummaryQueryOptions());
  const hours = useQuery(myHoursQueryOptions());
  const events = useQuery(recentEventsQueryOptions());
  const desks = useQuery(desksQueryOptions());

  const activeDesks = (desks.data ?? []).filter((d) => d.active);
  const deskCounts = useQuery(deskOpenCountsQueryOptions(activeDesks.map((d) => d.id)));

  const firstName = user?.name.trim().split(/\s+/)[0] ?? '';
  const todayLabel = new Date().toLocaleDateString('pt-BR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  const countByDesk = new Map((deskCounts.data ?? []).map((c) => [c.deskId, c.count]));
  const deskRows = activeDesks
    .map((d) => ({ id: d.id, name: d.displayName, count: countByDesk.get(d.id) ?? 0 }))
    .sort((a, b) => b.count - a.count);
  const maxDeskCount = Math.max(1, ...deskRows.map((r) => r.count));

  const showActivity = events.isPending || (events.data !== null && events.data !== undefined);

  return (
    <div className="space-y-4">
      {/* Saudação */}
      <header>
        <h1 className="text-xl leading-7 font-semibold">
          Bom te ver de volta{firstName ? `, ${firstName}` : ''}
        </h1>
        <p className="mt-0.5 text-[13px] text-muted-foreground first-letter:uppercase">
          {todayLabel}
        </p>
      </header>

      {/* Resumo de tickets */}
      <section className="rounded-lg border bg-card p-4 shadow-xs">
        <h2 className="mb-3 text-base font-semibold">Resumo de tickets</h2>
        {summary.isError ? (
          <EmptyState className="h-24" message="Erro ao carregar o resumo de tickets." />
        ) : (
          <div className="flex flex-wrap gap-x-10 gap-y-4">
            <Kpi
              icon={Ticket}
              label="Abertos"
              value={summary.data?.open}
              colorClass="text-primary"
              loading={summary.isPending}
            />
            <Kpi
              icon={UserRound}
              label="Meus"
              value={summary.data?.mine}
              colorClass="text-warning"
              loading={summary.isPending}
            />
            <Kpi
              icon={CircleDashed}
              label="Sem responsável"
              value={summary.data?.unassigned}
              colorClass="text-info"
              loading={summary.isPending}
            />
            <Kpi
              icon={Timer}
              label="Timers ativos"
              value={summary.data?.runningTimers}
              colorClass="text-success"
              loading={summary.isPending}
            />
            <Kpi
              icon={CheckCircle2}
              label="Fechados hoje"
              value={summary.data?.closedToday}
              colorClass="text-muted-foreground"
              loading={summary.isPending}
            />
          </div>
        )}
      </section>

      {/* Minha agenda da semana (spec 039) — acima de Últimas atividades */}
      <MyWeekAgendaSection />

      <div className="grid items-start gap-4 lg:grid-cols-2">
        {/* Últimas atividades */}
        {showActivity ? (
          <section className="rounded-lg border bg-card p-4 shadow-xs">
            <h2 className="mb-2 text-base font-semibold">Últimas atividades</h2>
            {events.isPending ? (
              <div className="space-y-3 py-1">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <Skeleton className="size-7 rounded-full" />
                    <div className="flex-1 space-y-1.5">
                      <Skeleton className="h-3.5 w-2/5" />
                      <Skeleton className="h-3 w-4/5" />
                    </div>
                  </div>
                ))}
              </div>
            ) : events.data && events.data.length > 0 ? (
              <ul>
                {events.data.map((e) => (
                  <ActivityItem key={e.id} event={e} />
                ))}
              </ul>
            ) : (
              <EmptyState className="h-28" message="Não há dados" />
            )}
          </section>
        ) : null}

        <div className={cn('space-y-4', !showActivity && 'lg:col-span-2')}>
          {/* Minhas horas apontadas */}
          <section className="rounded-lg border bg-card p-4 shadow-xs">
            <h2 className="mb-3 text-base font-semibold">Minhas horas apontadas</h2>
            {hours.isError ? (
              <EmptyState className="h-20" message="Erro ao carregar horas apontadas." />
            ) : (
              <div className="flex flex-wrap gap-x-10 gap-y-4">
                <HoursStat
                  icon={CalendarCheck2}
                  label="Ontem"
                  minutes={hours.data?.yesterdayMinutes}
                  colorClass="text-primary"
                  loading={hours.isPending}
                />
                <HoursStat
                  icon={Clock}
                  label="Hoje"
                  minutes={hours.data?.todayMinutes}
                  colorClass="text-warning"
                  loading={hours.isPending}
                />
                <HoursStat
                  icon={CalendarDays}
                  label="Semana"
                  minutes={hours.data?.weekMinutes}
                  colorClass="text-info"
                  loading={hours.isPending}
                />
              </div>
            )}
          </section>

          {/* Tickets por mesa */}
          <section className="rounded-lg border bg-card p-4 shadow-xs">
            <h2 className="mb-2 text-base font-semibold">Tickets por mesa</h2>
            {desks.isPending || (activeDesks.length > 0 && deskCounts.isPending) ? (
              <div className="space-y-3 py-1">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="space-y-1.5">
                    <Skeleton className="h-3.5 w-1/3" />
                    <Skeleton className="h-1.5 w-full rounded-full" />
                  </div>
                ))}
              </div>
            ) : desks.isError || deskCounts.isError ? (
              <EmptyState className="h-20" message="Erro ao carregar contagens por mesa." />
            ) : deskRows.length === 0 ? (
              <EmptyState className="h-20" message="Nenhuma mesa ativa." />
            ) : (
              <ul>
                {deskRows.map((row) => (
                  <li key={row.id} className="border-b border-divider py-2 last:border-0 last:pb-0">
                    <div className="mb-1 flex items-center justify-between gap-4 text-sm">
                      <span className="truncate">{row.name}</span>
                      <span
                        className={cn(
                          'font-semibold tabular-nums',
                          row.count === 0 && 'text-muted-foreground'
                        )}
                      >
                        {row.count}
                      </span>
                    </div>
                    <div className="h-1.5 overflow-hidden rounded-full bg-accent">
                      <div
                        className="h-full rounded-full bg-primary transition-[width]"
                        style={{ width: `${Math.round((row.count / maxDeskCount) * 100)}%` }}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
